import type {Canvas, CanvasEvents} from '@postermywall/fabricjs-2';
import {Point} from '@postermywall/fabricjs-2';
import {GUIDELINES_COLOR, GUIDELINES_MARGIN} from '@PosterWhiteboard/page/page.types';

/**
 * Augments canvas by assigning to `onObjectMove` and `onAfterRender`.
 * This kind of sucks because other code using those methods will stop functioning.
 * Need to fix it by replacing callbacks with pub/sub kind of subscription model.
 * (or maybe use existing fabric.util.fire/observe (if it won't be too slow))
 */
export function initCenteringGuidelines(canvas: Canvas) {
  const isPosterEditorCanvas = canvas.lowerCanvasEl.closest('#poster-whiteboard-container') !== null;

  const getTotalNonZoomedWidth = (): number => {
    if (isPosterEditorCanvas) {
      return window.posterEditor?.whiteboard?.width ?? 0;
    }

    return canvas.getWidth() / canvas.getZoom();
  };

  const getTotalNonZoomedHeight = (): number => {
    if (isPosterEditorCanvas) {
      return window.posterEditor?.whiteboard?.height ?? 0;
    }

    return canvas.getHeight() / canvas.getZoom();
  };

  let canvasWidth = getTotalNonZoomedWidth();
  let canvasHeight = getTotalNonZoomedHeight();
  let canvasWidthCenter = canvasWidth / 2;
  let canvasHeightCenter = canvasHeight / 2;
  let canvasWidthCenterMap: Record<number, true> = {};
  let canvasHeightCenterMap: Record<number, true> = {};

  const centerLineMargin = GUIDELINES_MARGIN;
  const centerLineWidth = 1;
  const ctx = canvas.getSelectionContext();

  const showVerticalCenterLine = (): void => {
    showCenterLine(canvasWidthCenter + 0.5, 0, canvasWidthCenter + 0.5, canvasHeight);
  };

  const showHorizontalCenterLine = (): void => {
    showCenterLine(0, canvasHeightCenter + 0.5, canvasWidth, canvasHeightCenter + 0.5);
  };

  const showCenterLine = (x1: number, y1: number, x2: number, y2: number): void => {
    const zoom = canvas.getZoom();
    const viewportTransform = canvas.viewportTransform;

    ctx.save();
    ctx.transform(...viewportTransform);
    ctx.strokeStyle = GUIDELINES_COLOR;
    ctx.lineWidth = centerLineWidth / zoom;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.restore();
  };

  let isInVerticalCenter = false;
  let isInHorizontalCenter = false;

  function mouseDown(): void {
    let i;
    let len;

    canvasWidth = getTotalNonZoomedWidth();
    canvasWidthCenter = canvasWidth / 2;
    canvasWidthCenterMap = {};
    for (i = canvasWidthCenter - centerLineMargin, len = canvasWidthCenter + centerLineMargin; i <= len; i++) {
      canvasWidthCenterMap[Math.round(i)] = true;
    }

    canvasHeight = getTotalNonZoomedHeight();
    canvasHeightCenter = canvasHeight / 2;
    canvasHeightCenterMap = {};
    for (i = canvasHeightCenter - centerLineMargin, len = canvasHeightCenter + centerLineMargin; i <= len; i++) {
      canvasHeightCenterMap[Math.round(i)] = true;
    }
  }

  function moving(e: CanvasEvents['object:moving']): void {
    const object = e.target,
      objectCenter = object.getCenterPoint(),
      transform = canvas._currentTransform;

    if (!transform) return;

    isInVerticalCenter = Math.round(objectCenter.x) in canvasWidthCenterMap;
    isInHorizontalCenter = Math.round(objectCenter.y) in canvasHeightCenterMap;

    if (isInHorizontalCenter || isInVerticalCenter) {
      object.setPositionByOrigin(
        new Point(isInVerticalCenter ? canvasWidthCenter : objectCenter.x, isInHorizontalCenter ? canvasHeightCenter : objectCenter.y),
        'center',
        'center'
      );
    }
  }

  function beforeRender(): void {
    // The in-progress free-drawing stroke lives on contextTop until mouse-up; clearing it mid-stroke wipes the drawing
    if (canvas.contextTop && !canvas.isDrawingMode) {
      canvas.clearContext(canvas.contextTop);
    }
  }

  function afterRender(): void {
    if (isInVerticalCenter) {
      showVerticalCenterLine();
    }
    if (isInHorizontalCenter) {
      showHorizontalCenterLine();
    }
  }

  function mouseUp(): void {
    isInVerticalCenter = isInHorizontalCenter = false;
    canvas.renderAll();
  }

  canvas.on('object:moving', moving);
  canvas.on('before:render', beforeRender);
  canvas.on('after:render', afterRender);
  canvas.on('mouse:up', mouseUp);
  canvas.on('mouse:down', mouseDown);

  return (): void => {
    canvas.off('object:moving', moving);
    canvas.off('before:render', beforeRender);
    canvas.off('after:render', afterRender);
    canvas.off('mouse:up', mouseUp);
    canvas.off('mouse:down', mouseDown);
  };
}
